/**
 * A custom element for enhancing a navigation menu link into the an expandable navigation menu component.
 *
 * @module iabbb-nav-menu
 */
class NavMenu extends HTMLElement {
  connectedCallback() {
    this.moveHeaderMenu();
    this.replaceLinkWithButton();

    document.addEventListener('keydown', this.escapeHandler);
    this.addEventListener('click', this.clickEventHandler);

    this.collapseSubnavs();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.escapeHandler);
    document.removeEventListener('click', this.lightDismissHandler);
    this.removeEventListener('click', this.clickEventHandler);
  }

  clickEventHandler = (event) => {
    const subnavButton = event.target.closest('.has-subnav button');

    if (subnavButton) {
      this.toggleSubnav(subnavButton);
      return;
    }
  };

  /**
   * Emit a custom event
   * @param {string} type - The event type
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (type, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(`${this.localName}:${type}`, {
      bubbles: true,
      cancelable: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  collapseSubnavs = () => {
    for (const liWithSubnav of this.querySelectorAll('.has-subnav')) {
      const linkId = liWithSubnav.querySelector('a').getAttribute('id');

      const expandBtn = document.createElement('button');
      expandBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em"><path d="M241 337c-9.4 9.4-24.6 9.4-33.9 0L47 177c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l143 143L367 143c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9L241 337z"></path></svg>`;
      expandBtn.setAttribute('aria-expanded', 'false');
      expandBtn.setAttribute('aria-labelledby', linkId);

      liWithSubnav.insertBefore(expandBtn, liWithSubnav.querySelector('ul'));
    }

    for (const mainNavLink of this.querySelectorAll('#headerNavMenu ul > li a')) {
      mainNavLink.addEventListener('click', (e) => {
        this.emit('click-main-link', { label: e.currentTarget.innerText });
      });
    }

    for (const subNavLink of this.querySelectorAll('#headerNavMenu ul > li ul a')) {
      subNavLink.addEventListener('click', (e) => {
        const parentElement = e.currentTarget.closest('li').closest('ul').closest('li');

        if (!parentElement) {
          return;
        }

        this.emit('click-sub-link', { label: e.currentTarget.innerText, parentLabel: parentElement.innerText });
      });
    }
  };

  replaceLinkWithButton = () => {
    const link = this.querySelector('[href="#headerNavMenu"]');

    if (!link) {
      return;
    }

    const btn = document.createElement('button');
    btn.innerHTML = link.innerHTML;
    btn.className = link.className;
    btn.setAttribute('aria-expanded', 'false');
    link.replaceWith(btn);
    btn.addEventListener('click', this.toggleMenu);
  };

  moveHeaderMenu() {
    const navMenu = document.getElementById('headerNavMenu');
    this.querySelector('nav').append(navMenu);
  }

  toggleMenu = () => {
    const isOpen = this.querySelector('button').getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.removeEventListener('focusout', this.focusOutHandler);
      document.removeEventListener('click', this.lightDismissHandler);
      this.querySelector('nav').removeAttribute('data-open');
      this.querySelector('button').setAttribute('aria-expanded', 'false');
      this.trackMenuClose();
    } else {
      document.addEventListener('keydown', this.escapeHandler);
      this.addEventListener('focusout', this.focusOutHandler);
      document.addEventListener('click', this.lightDismissHandler);
      this.querySelector('nav').setAttribute('data-open', 'true');
      this.querySelector('button').setAttribute('aria-expanded', 'true');
      this.trackMenuOpen();
    }
  };

  trackMenuOpen = () => {
    this.querySelector('button').classList.remove('dtm-header-menu-button-closed');
    this.querySelector('button').classList.add('dtm-header-menu-button-opened');

    this.emit('expanded');
  };

  trackMenuClose = () => {
    this.querySelector('button').classList.remove('dtm-header-menu-button-opened');
    this.querySelector('button').classList.add('dtm-header-menu-button-closed');

    this.emit('collapsed');
  };

  toggleSubnav = (node) => {
    const isCurrentlyExpanded = node.getAttribute('aria-expanded');

    this.emit('toggle-subnav', {
      label: node.innerText,
      isCurrentlyExpanded,
    });

    node.setAttribute('aria-expanded', isCurrentlyExpanded === 'false' ? 'true' : 'false');
  };

  escapeHandler = (e) => {
    if (e.isComposing) {
      return;
    }

    const isOpen = this.querySelector('button').getAttribute('aria-expanded') === 'true';

    if (e.key === 'Escape' && isOpen) {
      this.toggleMenu();
    }
  };

  lightDismissHandler = (event) => {
    if (!event.composedPath().includes(this)) {
      this.toggleMenu();
    }
  };

  focusOutHandler = (event) => {
    const menuContainsFocus = this.contains(event.relatedTarget);
    if (!menuContainsFocus) {
      this.toggleMenu();
    }
  };
}

customElements.define('iabbb-nav-menu', NavMenu);
